import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiPermits } from '@/lib/api';
import { PERMIT_TYPES } from '@/constants/permits';
import { Q_EPI_LIST } from '@/components/AdtEpiSection';
import { X, Calendar, MapPin, Building2, AlertOctagon, Shield } from 'lucide-react';

const PICTO_SRC = {};
Q_EPI_LIST.forEach((cat) => cat.items.forEach((it) => { PICTO_SRC[it.id] = { src: it.src, label: it.label }; }));

export default function BriefingMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [permit, setPermit] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    apiPermits.get(id).then(r => setPermit(r.data)).catch(() => navigate('/permits'));
    // Hide scrollbars
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, [id, navigate]);

  if (!permit) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Chargement...</div>;

  const typeInfo = PERMIT_TYPES.find((t) => t.id === permit.type) || {};
  const epiSelected = Object.entries(permit.extra?.adt_epi || {}).filter(([, v]) => v).map(([k]) => PICTO_SRC[k]).filter(Boolean);
  const risksOuverts = Object.entries(permit.extra?.risk_blocks || {}).filter(([, arr]) => Array.isArray(arr) && arr.some(Boolean));
  const sopSteps = permit.extra?.sop_steps || [];

  const pages = [
    {
      title: 'INTERVENTION',
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="text-[14px] font-bold uppercase tracking-[0.3em] text-stone-400 mb-2">{typeInfo.label || permit.type}</div>
            <div className="text-6xl lg:text-8xl font-display font-black tracking-tighter">{permit.titre}</div>
            <div className="text-stone-300 text-2xl mt-4 font-mono">{permit.numero}</div>
          </div>
          <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto pt-12">
            <div className="border-2 border-stone-700 p-6">
              <Building2 className="w-8 h-8 text-[#DA291C] mb-2" />
              <div className="text-stone-400 uppercase text-xs tracking-wider">Site</div>
              <div className="text-2xl font-bold">{permit.site_code}</div>
            </div>
            <div className="border-2 border-stone-700 p-6">
              <MapPin className="w-8 h-8 text-[#DA291C] mb-2" />
              <div className="text-stone-400 uppercase text-xs tracking-wider">Lieu</div>
              <div className="text-2xl font-bold">{permit.lieu}</div>
            </div>
            <div className="border-2 border-stone-700 p-6">
              <Calendar className="w-8 h-8 text-[#DA291C] mb-2" />
              <div className="text-stone-400 uppercase text-xs tracking-wider">Période</div>
              <div className="text-xl font-bold">{permit.date_debut?.slice(0, 16).replace('T', ' ')}</div>
              <div className="text-sm text-stone-400">→ {permit.date_fin?.slice(0, 16).replace('T', ' ')}</div>
            </div>
            <div className="border-2 border-stone-700 p-6">
              <Building2 className="w-8 h-8 text-[#DA291C] mb-2" />
              <div className="text-stone-400 uppercase text-xs tracking-wider">Entreprise</div>
              <div className="text-xl font-bold">{permit.entreprise_exterieure || 'Lafarge (interne)'}</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'EPI OBLIGATOIRES',
      content: (
        <div>
          <h2 className="text-center text-3xl font-display font-black mb-8 uppercase tracking-wider">EPI à porter pendant l'intervention</h2>
          {epiSelected.length > 0 ? (
            <div className="grid grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
              {epiSelected.map((e, i) => (
                <div key={i} className="bg-white p-6 flex flex-col items-center">
                  <img src={e.src} alt={e.label} className="w-32 h-32 object-contain" />
                  <div className="mt-4 text-black text-center text-lg font-bold uppercase">{e.label}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-stone-400 text-xl">EPI minimum obligatoires uniquement (Casque, Lunettes, Chaussures, Gants, HV, Auditive)</div>
          )}
        </div>
      ),
    },
    {
      title: 'RISQUES',
      content: (
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center text-3xl font-display font-black mb-8 uppercase tracking-wider">Risques identifiés sur le chantier</h2>
          {risksOuverts.length > 0 ? (
            <div className="grid grid-cols-2 gap-4">
              {risksOuverts.map(([blockId, items]) => (
                <div key={blockId} className="bg-red-700 p-6 border-2 border-red-900">
                  <div className="text-xs font-bold uppercase tracking-wider text-red-200 mb-2">Bloc {blockId}</div>
                  <AlertOctagon className="w-10 h-10 text-white mb-2" />
                  <ul className="space-y-1">
                    {items.map((checked, idx) => checked && (
                      <li key={idx} className="text-white text-base font-semibold flex gap-2">
                        <span>•</span> Risque actif #{idx + 1}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : sopSteps.some(s => s.risques_mesures) ? (
            <div className="space-y-4">
              {sopSteps.filter(s => s.risques_mesures).map((s, i) => (
                <div key={i} className="bg-stone-800 border-l-4 border-[#DA291C] p-6">
                  <div className="text-xs uppercase tracking-wider text-stone-400 mb-1">Étape {i + 1}</div>
                  <div className="text-xl font-semibold">{s.sous_etapes}</div>
                  <div className="text-stone-300 mt-2">{s.risques_mesures}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-stone-400 text-xl">Aucun risque majeur déclaré</div>
          )}
        </div>
      ),
    },
    {
      title: 'INTERVENANTS',
      content: (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-3xl font-display font-black mb-8 uppercase tracking-wider">Personnes intervenantes</h2>
          {permit.intervenants?.length > 0 ? (
            <div className="space-y-3">
              {permit.intervenants.map((p, i) => (
                <div key={i} className="bg-stone-800 border-l-4 border-[#DA291C] p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#DA291C] flex items-center justify-center text-2xl font-bold">{i + 1}</div>
                  <div className="text-2xl font-semibold">{p}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-stone-400 text-xl">Aucun intervenant déclaré</div>
          )}
        </div>
      ),
    },
  ];

  const cur = pages[page];

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col z-50" data-testid="briefing-mode">
      <header className="px-8 py-4 border-b border-stone-700 flex items-center justify-between bg-stone-900">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-400">Briefing terrain</div>
          <div className="text-lg font-display font-bold">{cur.title}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-stone-400">{page + 1} / {pages.length}</div>
          <button
            data-testid="briefing-close"
            onClick={() => navigate(`/permits/${id}`)}
            className="w-10 h-10 border-2 border-stone-700 flex items-center justify-center hover:bg-[#DA291C] hover:border-[#DA291C]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-8 lg:p-12 flex items-center justify-center">
        <div className="w-full">{cur.content}</div>
      </main>

      <footer className="px-8 py-4 border-t border-stone-700 flex items-center justify-between bg-stone-900">
        <button
          data-testid="briefing-prev"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="px-6 py-3 border-2 border-stone-700 font-bold uppercase tracking-wider text-sm hover:bg-stone-800 disabled:opacity-30"
        >
          ← Précédent
        </button>
        <div className="flex gap-2">
          {pages.map((_, i) => (
            <button
              key={i}
              data-testid={`briefing-dot-${i}`}
              onClick={() => setPage(i)}
              className={`w-3 h-3 rounded-full transition-all ${i === page ? 'bg-[#DA291C] w-8' : 'bg-stone-600'}`}
            />
          ))}
        </div>
        <button
          data-testid="briefing-next"
          onClick={() => setPage((p) => Math.min(pages.length - 1, p + 1))}
          disabled={page === pages.length - 1}
          className="px-6 py-3 bg-[#DA291C] font-bold uppercase tracking-wider text-sm hover:bg-red-700 disabled:opacity-30"
        >
          Suivant →
        </button>
      </footer>
    </div>
  );
}
